import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Info, Ban } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface AdminPostInfoProps {
  post: any;
}

export const AdminPostInfo: React.FC<AdminPostInfoProps> = ({ post }) => {
  const handleBanIP = async () => {
    if (!post.ip_address) {
      toast({
        title: "No IP address available",
        description: "Cannot ban IP - no address recorded for this post",
        variant: "destructive"
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('banned_ips')
        .insert({
          ip_address: post.ip_address,
          ban_type: 'temporary',
          reason: `Spam detected from post ${post.id}`,
          expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24 hours
          admin_notes: `Auto-banned from post review for spam activity`
        });

      if (error) throw error;

      toast({
        title: "IP Address Banned",
        description: `Successfully banned ${post.ip_address} for 24 hours`,
      });
    } catch (error) {
      console.error('Error banning IP:', error);
      toast({
        title: "Ban failed",
        description: "Failed to ban IP address",
        variant: "destructive"
      });
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-6 w-6 p-0 text-muted-foreground hover:text-blue-500 hover:bg-blue-500/10"
          onClick={(e) => {
            e.stopPropagation();
            console.log('Post info clicked for post:', post.id);
          }}
        >
          <Info className="h-3 w-3" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Post Information</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <span className="font-medium">Author:</span>
            <span className="ml-2">
              {post.profiles?.username || 'Anonymous'}
            </span>
          </div>
          
          <div>
            <span className="font-medium">IP Address:</span>
            <div className="flex items-center gap-2 mt-1">
              <span className="font-mono text-xs bg-muted px-2 py-1 rounded border-l-4 border-orange-500">
                {post.ip_address || 'Not recorded'}
              </span>
              {post.ip_address && (
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={handleBanIP}
                  className="h-6 px-2 text-xs"
                >
                  <Ban className="h-3 w-3 mr-1" />
                  Ban IP
                </Button>
              )}
            </div>
          </div>
          
          <div>
            <span className="font-medium">Created:</span>
            <span className="ml-2">
              {formatDistanceToNow(new Date(post.created_at))} ago
            </span>
          </div>
          
          {post.updated_at !== post.created_at && (
            <div>
              <span className="font-medium">Last edited:</span>
              <span className="ml-2">
                {formatDistanceToNow(new Date(post.updated_at))} ago
              </span>
            </div>
          )}
          
          <div>
            <span className="font-medium">Post ID:</span>
            <span className="ml-2 font-mono text-xs bg-muted px-2 py-1 rounded">
              {post.id}
            </span>
          </div>
          
          <div>
            <span className="font-medium">Anonymous:</span>
            <span className="ml-2">
              {post.is_anonymous ? 'Yes' : 'No'}
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};